/*eslint max-lines-per-function: ["error", 185]*/
import React, { useEffect, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { Eye } from 'react-bootstrap-icons';
import { isEqual } from 'lodash';

import ValidationAlert from '../../../app/components/ValidationAlert';
import Button from '../../../common/components/Button/Button';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { useNotifications } from '../../../common/hooks/useNotifications';
import {
  CreateUpdateSurveyEmailContentDto,
  GetSurveyEmailContentDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { SurveyEditorTabs } from '../../containers/SurveyEditorView/SurveyEditorView';
import { validateEmailData } from '../../../common/pokCore/contexts/SurveyContext';

import { PlaceholdersSection } from './PlaceholdersSection';
import { EmailFormFields } from './EmailFormFields';
import { EmailPreviewModal } from './EmailPreviewModal';

interface SurveyEmailTemplateProps {
  surveyId: string;
  isLocked?: boolean;
  isOtherTabInEditMode?: boolean;
  originalEmailData: GetSurveyEmailContentDto;
  setNotSavedTab: (tab: SurveyEditorTabs | undefined) => void;
}

export const SurveyEmailTemplate: React.FC<
  SurveyEmailTemplateProps
> = props => {
  const pok = usePokCore();
  const { t, tk } = useTranslation('administrationLng');
  const notifications = useNotifications();

  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [copiedTag, setCopiedTag] = useState<string | null>(null);

  const [emailData, setEmailData] = useState<CreateUpdateSurveyEmailContentDto>(
    props.originalEmailData,
  );
  const [showPreview, setShowPreview] = useState(false);

  const prevOriginalDataRef = useRef(props.originalEmailData);
  const readOnly = props.isLocked || props.isOtherTabInEditMode;

  useEffect(() => {
    if (!isEqual(prevOriginalDataRef.current, props.originalEmailData)) {
      setEmailData(props.originalEmailData);
      prevOriginalDataRef.current = props.originalEmailData;
    }
  }, [props.originalEmailData]);

  const copyToClipboard = async (tag: string) => {
    try {
      await navigator.clipboard.writeText(tag);
      setCopiedTag(tag);
      setTimeout(() => setCopiedTag(null), 3000);
    } catch {
      // Ignore clipboard errors
    }
  };

  const showEmailPreview = async () => {
    setShowPreview(true);
  };

  const placeholders = [
    {
      tag: '{{url}}',
      description: t(tk.survey.emailTemplatePlaceholderUrl),
      required: true,
      sampleData:
        '<a href="https://example.com/survey-link">Wypełnij ankietę</a>',
      sampleDataEnglish:
        '<a href="https://example.com/survey-link">Fill out the survey</a>',
    },
    {
      tag: '{{companyName}}',
      description: t(tk.survey.emailTemplatePlaceholderCompany),
      required: false,
      sampleData: 'ValueMedia',
      sampleDataEnglish: 'ValueMedia',
    },
    {
      tag: '{{companyNameLong}}',
      description: t(tk.survey.emailTemplatePlaceholderCompanyLong),
      required: false,
      sampleData: 'ValueMedia sp z o.o.',
      sampleDataEnglish: 'ValueMedia sp z o.o.',
    },
    {
      tag: '{{responsible}}',
      description: t(tk.survey.emailTemplatePlaceholderResponsible),
      required: false,
      sampleData: 'Jan Kowalski',
      sampleDataEnglish: 'John Doe',
    },
  ];

  const propertyChange = (obj: Partial<CreateUpdateSurveyEmailContentDto>) => {
    const updatedEmailData = {
      ...emailData,
      ...obj,
    };

    const dataChanged = !isEqual(updatedEmailData, props.originalEmailData);

    props.setNotSavedTab(dataChanged ? SurveyEditorTabs.EMAIL : undefined);
    if (isEqual(updatedEmailData, emailData)) {
      return;
    }

    setEmailData(updatedEmailData);
  };

  const handleSave = async () => {
    const validation = await validateEmailData(emailData);
    if (!validation.valid) {
      setSaveErrors(validation.errors);
      setShowSaveErrors(true);
      return;
    }

    setSaving(true);
    setSaveErrors([]);
    setShowSaveErrors(false);

    try {
      await pok.surveys.updateEmailTemplate(props.surveyId, emailData);
      props.setNotSavedTab(undefined);
      notifications.saveCompleted();
    } catch (error) {
      notifications.caughtError(error as ResponseError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PlaceholdersSection
        placeholders={placeholders}
        copiedTag={copiedTag}
        copyToClipboard={copyToClipboard}
      />

      <EmailFormFields
        emailData={emailData}
        propertyChange={propertyChange}
        readOnly={readOnly}
      />

      <ValidationAlert
        show={showSaveErrors}
        errors={saveErrors}
        className="mt-3"
      />

      {!readOnly && (
        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button variant="outline-primary" onClick={showEmailPreview}>
            <Eye className="me-2" />
            {t(tk.survey.emailTemplatePreview)}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            isLoading={saving}
            disabled={saving}
          >
            {t(tk.button.save)}
          </Button>
        </div>
      )}

      {props.isLocked && (
        <Alert variant="warning" className="mt-3">
          {t(tk.survey.surveyLockedInfo)}
        </Alert>
      )}
      <EmailPreviewModal
        show={showPreview}
        onHide={() => {
          setShowPreview(false);
        }}
        emailData={emailData}
        placeholders={placeholders}
      />
    </>
  );
};
